/**
 * Phase 2.14 — Telecom Vendor Exposure (Reviewer Console).
 *
 * Reviewer-managed panel placed next to Operating & Regulatory Context.
 * Risk-based, neutral framing — CSV Pro does not designate vendors as
 * restricted or prohibited.
 *
 * - Add/Edit uses INLINE expandable row (no modal) so reviewers can
 *   enter and compare multiple vendors quickly.
 * - Mark Reviewed is INDEPENDENT from operating context confirmation.
 * - Editing or deleting a vendor automatically clears confirmation
 *   (handled server-side in the upsert/delete RPCs).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Plus, Loader2, ShieldQuestion } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  VENDOR_TYPE_OPTIONS,
  TELECOM_DOMAIN_OPTIONS,
  DEPLOYMENT_SCOPE_OPTIONS,
  DEPENDENCY_LEVEL_OPTIONS,
  HIGH_RISK_FLAG_OPTIONS,
  EVIDENCE_SOURCE_OPTIONS,
  VENDOR_EXPOSURE_DISCLAIMER,
  vendorTypeLabel,
  telecomDomainLabel,
  telecomDomainsLabel,
  deploymentScopeLabel,
  dependencyLevelLabel,
  highRiskFlagLabel,
  evidenceSourceLabel,
  resolveVendorDomains,
  buildDisruptionImpact,
  type VendorExposureRow,
} from "@/reports/telecom/vendorExposure";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";


const sb: any = supabase as any;

type EditDraft = {
  id: string | null;
  vendor_name: string;
  vendor_type: string;
  telecom_domains: string[];
  deployment_scope: string;
  dependency_level: string;
  estimated_percentage: string;
  high_risk_flag: string;
  evidence_source: string;
  reviewer_notes: string;
  disruption_impact_override: string;
};

const EMPTY_DRAFT: EditDraft = {
  id: null,
  vendor_name: "",
  vendor_type: "",
  telecom_domains: [],
  deployment_scope: "",
  dependency_level: "",
  estimated_percentage: "",
  high_risk_flag: "",
  evidence_source: "",
  reviewer_notes: "",
  disruption_impact_override: "",
};


interface Props {
  assessmentId: string;
  disabled?: boolean;
  /** Initial confirmation state (from reviewer_get_assessment_profile). */
  initialConfirmed?: boolean;
  /** Notify parent so the parent profile state stays in sync. */
  onConfirmationChange?: (confirmed: boolean) => void;
}

export function TelecomVendorExposurePanel({
  assessmentId,
  disabled,
  initialConfirmed,
  onConfirmationChange,
}: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [vendors, setVendors] = useState<VendorExposureRow[]>([]);
  const [confirmed, setConfirmed] = useState<boolean>(!!initialConfirmed);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  useEffect(() => {
    setConfirmed(!!initialConfirmed);
  }, [initialConfirmed]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb.rpc("reviewer_list_telecom_vendor_exposure", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      setVendors((Array.isArray(data) ? data : []) as VendorExposureRow[]);
    } catch (e: any) {
      toast({
        title: "Could not load vendor exposure",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [assessmentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => setDraft({ ...EMPTY_DRAFT });
  const startEdit = (v: VendorExposureRow) =>
    setDraft({
      id: v.id,
      vendor_name: v.vendor_name ?? "",
      vendor_type: v.vendor_type ?? "",
      telecom_domains: resolveVendorDomains(v),
      deployment_scope: v.deployment_scope ?? "",
      dependency_level: v.dependency_level ?? "",
      estimated_percentage:
        v.estimated_percentage != null ? String(v.estimated_percentage) : "",
      high_risk_flag: v.high_risk_flag ?? "",
      evidence_source: v.evidence_source ?? "",
      reviewer_notes: v.reviewer_notes ?? "",
      disruption_impact_override: v.disruption_impact_override ?? "",
    });


  const cancel = () => setDraft(null);

  const saveDraft = useCallback(async () => {
    if (!draft) return;
    if (!draft.vendor_name.trim()) {
      toast({
        title: "Vendor name required",
        description: "Enter a vendor name before saving.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const pct = draft.estimated_percentage.trim();
      const pctNum = pct === "" ? null : Number(pct);
      if (pct !== "" && (!Number.isFinite(pctNum) || (pctNum as number) < 0 || (pctNum as number) > 100)) {
        throw new Error("Estimated percentage must be between 0 and 100.");
      }
      const { error } = await sb.rpc("reviewer_upsert_telecom_vendor_exposure", {
        p_id: draft.id,
        p_assessment_id: assessmentId,
        p_vendor_name: draft.vendor_name.trim(),
        p_vendor_type: draft.vendor_type || null,
        p_telecom_domains: draft.telecom_domains.length > 0 ? draft.telecom_domains : null,
        p_deployment_scope: draft.deployment_scope || null,
        p_dependency_level: draft.dependency_level || null,
        p_estimated_percentage: pctNum,
        p_high_risk_flag: draft.high_risk_flag || null,
        p_evidence_source: draft.evidence_source || null,
        p_reviewer_notes: draft.reviewer_notes.trim() || null,
        p_disruption_impact_override: draft.disruption_impact_override.trim() || null,
      });
      if (error) throw error;
      setDraft(null);
      // Server clears confirmation when editing; reflect that locally.
      if (confirmed) {
        setConfirmed(false);
        onConfirmationChange?.(false);
      }
      await load();
      toast({ title: "Vendor saved" });
    } catch (e: any) {
      toast({
        title: "Could not save vendor",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [draft, assessmentId, confirmed, onConfirmationChange, load, toast]);

  const deleteVendor = useCallback(
    async (id: string) => {
      if (!window.confirm("Delete this vendor entry?")) return;
      try {
        const { error } = await sb.rpc("reviewer_delete_telecom_vendor_exposure", {
          p_id: id,
        });
        if (error) throw error;
        if (confirmed) {
          setConfirmed(false);
          onConfirmationChange?.(false);
        }
        await load();
        toast({ title: "Vendor deleted" });
      } catch (e: any) {
        toast({
          title: "Could not delete vendor",
          description: e?.message ?? "Unknown error",
          variant: "destructive",
        });
      }
    },
    [confirmed, onConfirmationChange, load, toast],
  );

  const toggleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      const next = !confirmed;
      const { data, error } = await sb.rpc("reviewer_set_vendor_exposure_confirmation", {
        p_assessment_id: assessmentId,
        p_confirmed: next,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const nextVal = !!row?.vendor_exposure_confirmed;
      setConfirmed(nextVal);
      onConfirmationChange?.(nextVal);
      toast({
        title: nextVal ? "Vendor exposure marked Reviewed" : "Confirmation cleared",
        description: nextVal
          ? "The report will render the vendor exposure section as reviewer-confirmed."
          : "The report will mark this section as draft / unconfirmed.",
      });
    } catch (e: any) {
      toast({
        title: "Could not update confirmation",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setConfirming(false);
    }
  }, [assessmentId, confirmed, onConfirmationChange, toast]);

  const pill = useMemo(() => {
    if (confirmed) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-green-800 dark:bg-green-900/40 dark:text-green-200">
          Reviewed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        Draft
      </span>
    );
  }, [confirmed]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldQuestion className="h-4 w-4 text-primary" />
              Technology & Vendor Exposure
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground italic max-w-3xl">
              {VENDOR_EXPOSURE_DISCLAIMER}
            </p>
          </div>
          <div className="flex items-center gap-2">{pill}</div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading vendors…
          </div>
        ) : vendors.length === 0 && !draft ? (
          <p className="text-sm text-muted-foreground italic">
            No vendor exposure information has been provided.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left py-2 pr-3">Vendor</th>
                  <th className="text-left py-2 pr-3">Domain</th>
                  <th className="text-left py-2 pr-3">Scope</th>
                  <th className="text-left py-2 pr-3">Dependency</th>
                  <th className="text-left py-2 pr-3">Disruption Impact</th>
                  <th className="text-right py-2 pr-1 w-[80px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b align-top hover:bg-muted/30">
                    <td className="py-2 pr-3 font-medium text-foreground">{v.vendor_name}</td>
                    <td className="py-2 pr-3" title={telecomDomainsLabel(resolveVendorDomains(v))}>
                      {telecomDomainsLabel(resolveVendorDomains(v))}
                    </td>
                    <td className="py-2 pr-3">{deploymentScopeLabel(v.deployment_scope)}</td>
                    <td className="py-2 pr-3">{dependencyLevelLabel(v.dependency_level)}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground italic">
                      {buildDisruptionImpact(v)}
                    </td>
                    <td className="py-2 pr-1 text-right">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={disabled || !!draft}
                        onClick={() => startEdit(v)}
                        title="Edit vendor"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={disabled || !!draft}
                        onClick={() => deleteVendor(v.id)}
                        title="Delete vendor"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        )}

        {draft && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              {draft.id ? "Edit Vendor" : "Add Vendor"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Vendor name *">
                <Input
                  value={draft.vendor_name}
                  onChange={(e) =>
                    setDraft({ ...draft, vendor_name: e.target.value })
                  }
                  placeholder="e.g. Vendor A"
                />
              </Field>
              <SelectField
                label="Vendor type"
                value={draft.vendor_type}
                onChange={(v) => setDraft({ ...draft, vendor_type: v })}
                options={VENDOR_TYPE_OPTIONS as any}
              />
              <MultiSelectField
                label="Telecom domains supported"
                values={draft.telecom_domains}
                onChange={(arr) => setDraft({ ...draft, telecom_domains: arr })}
                options={TELECOM_DOMAIN_OPTIONS as any}
              />

              <SelectField
                label="Deployment scope"
                value={draft.deployment_scope}
                onChange={(v) => setDraft({ ...draft, deployment_scope: v })}
                options={DEPLOYMENT_SCOPE_OPTIONS as any}
              />
              <SelectField
                label="Dependency level"
                value={draft.dependency_level}
                onChange={(v) => setDraft({ ...draft, dependency_level: v })}
                options={DEPENDENCY_LEVEL_OPTIONS as any}
              />
              <Field label="Estimated % supported (optional)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={draft.estimated_percentage}
                  onChange={(e) =>
                    setDraft({ ...draft, estimated_percentage: e.target.value })
                  }
                  placeholder="e.g. 35"
                />
              </Field>
              <SelectField
                label="High-risk vendor flag"
                value={draft.high_risk_flag}
                onChange={(v) => setDraft({ ...draft, high_risk_flag: v })}
                options={HIGH_RISK_FLAG_OPTIONS as any}
              />
              <SelectField
                label="Evidence source"
                value={draft.evidence_source}
                onChange={(v) => setDraft({ ...draft, evidence_source: v })}
                options={EVIDENCE_SOURCE_OPTIONS as any}
              />
            </div>
            <Field label="Reviewer notes">
              <Textarea
                rows={3}
                value={draft.reviewer_notes}
                onChange={(e) =>
                  setDraft({ ...draft, reviewer_notes: e.target.value })
                }
                placeholder="Neutral, risk-based observations…"
              />
            </Field>
            <Field label="Disruption impact (override)">
              <Textarea
                rows={2}
                value={draft.disruption_impact_override}
                onChange={(e) =>
                  setDraft({ ...draft, disruption_impact_override: e.target.value })
                }
                placeholder={
                  buildDisruptionImpact({
                    id: draft.id ?? "",
                    vendor_name: draft.vendor_name,
                    vendor_type: draft.vendor_type || null,
                    telecom_domain: draft.telecom_domains[0] ?? null,
                    telecom_domains: draft.telecom_domains,
                    deployment_scope: draft.deployment_scope || null,
                    dependency_level: draft.dependency_level || null,
                    estimated_percentage: null,
                    high_risk_flag: draft.high_risk_flag || null,
                    evidence_source: draft.evidence_source || null,
                    reviewer_notes: null,
                    disruption_impact_override: null,
                  })
                }
              />
              <p className="text-[11px] text-muted-foreground italic">
                Leave blank to use the auto-generated text shown above. ≤10 words recommended.
              </p>
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={disabled || saving || !draft.vendor_name.trim()}
                onClick={saveDraft}
              >
                {saving ? "Saving…" : draft.id ? "Update vendor" : "Add vendor"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={cancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
          {!draft && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={startAdd}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Vendor
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={confirmed ? "outline" : "default"}
            disabled={disabled || confirming || vendors.length === 0}
            onClick={toggleConfirm}
            title={
              vendors.length === 0
                ? "Add at least one vendor before marking Reviewed."
                : undefined
            }
          >
            {confirmed ? "Clear confirmation" : "Mark Vendor Exposure Reviewed"}
          </Button>
          <p className="text-[11px] text-muted-foreground italic">
            Confirmation is independent from Operating Context. Adding, editing,
            or deleting a vendor clears confirmation and requires re-review.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function MultiSelectField({
  label,
  values,
  onChange,
  options,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  const display =
    values.length === 0
      ? "Select…"
      : options
          .filter((o) => values.includes(o.value))
          .map((o) => o.label)
          .join(" / ");
  const toggle = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      // Preserve option order so the joined display reads predictably.
      const next = options.map((o) => o.value).filter((v) => v === val || values.includes(v));
      onChange(next);
    }
  };
  return (
    <Field label={label}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal"
          >
            <span className={values.length === 0 ? "text-muted-foreground" : ""}>
              {display}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-2" align="start">
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {options.map((o) => {
              const checked = values.includes(o.value);
              return (
                <label
                  key={o.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(o.value)}
                  />
                  <span>{o.label}</span>
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </Field>
  );
}

